// "Database backups" card on TenantDetailScreen: the tenant's dumps (read
// from its own object storage — S3 for a production tenant), a "Back up now"
// button, download, and Restore. Backed by routes/platform/backups.js.
//
// Restore is only offered while the tenant is suspended — enforced here AND
// server-side — because staff writing data during a restore would either
// block it or have their work silently overwritten.
import { useEffect, useState } from 'react';
import { platformApi, fetchPlatformBlob } from '../lib/platformApi/client';
import { RestoreBackupDialog } from './RestoreBackupDialog';
import { formatBytes } from './format';
import type { BackupKind, TenantBackup, TenantBackupList } from './types';

const KIND_LABEL: Record<BackupKind, { label: string; bg: string; color: string }> = {
  scheduled:   { label: 'Scheduled',   bg: '#F3F4F6', color: '#374151' },
  manual:      { label: 'Manual',      bg: '#DBEAFE', color: '#1E40AF' },
  pre_restore: { label: 'Pre-restore', bg: '#FEF3C7', color: '#92400E' },
  pre_delete:  { label: 'Pre-delete',  bg: '#FEE2E2', color: '#991B1B' },
};

const PROVIDER_LABEL: Record<string, string> = { s3: 'Amazon S3', gcs: 'Google Cloud Storage', local: 'Server disk' };

const btn = { background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' } as const;

interface Props {
  tenantId: number;
  slug: string;
  tenantStatus: string;
  /** Called after a restore, since it can change what the detail screen shows. */
  onChanged: () => void;
}

export function TenantBackupsPanel({ tenantId, slug, tenantStatus, onChanged }: Props) {
  const [data, setData] = useState<TenantBackupList | null>(null);
  const [error, setError] = useState('');
  const [backingUp, setBackingUp] = useState(false);
  const [notice, setNotice] = useState('');
  const [restoreTarget, setRestoreTarget] = useState<TenantBackup | null>(null);
  const [downloading, setDownloading] = useState('');

  const refresh = () => {
    platformApi.get<TenantBackupList>(`/tenants/${tenantId}/backups`)
      .then(d => { setData(d); setError(''); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(refresh, [tenantId, tenantStatus]);

  const backupNow = async () => {
    setBackingUp(true); setError(''); setNotice('');
    try {
      const r = await platformApi.post<{ key: string; bytes: number; storage_provider: string }>(`/tenants/${tenantId}/backups`);
      setNotice(`Backup saved: ${r.key} (${formatBytes(r.bytes)}, ${PROVIDER_LABEL[r.storage_provider] ?? r.storage_provider})`);
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setBackingUp(false);
  };

  const download = async (b: TenantBackup) => {
    setDownloading(b.key); setError('');
    try {
      const blob = await fetchPlatformBlob(`/tenants/${tenantId}/backups/download?key=${encodeURIComponent(b.key)}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = b.key.replace(/^backups\//, '');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setDownloading('');
  };

  const suspended = tenantStatus === 'suspended';
  const provider = data?.storage_provider ?? null;

  return (
    <div className="card" style={{ padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Database backups</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            Stored in this hospital's own object storage
            {provider && <> — <strong>{PROVIDER_LABEL[provider] ?? provider}</strong></>}
            {data?.last_backup_at && <> · last backup {new Date(data.last_backup_at).toLocaleString()}</>}
          </div>
        </div>
        <button className="btn-primary" onClick={backupNow} disabled={backingUp || !data} style={{ whiteSpace: 'nowrap' }}>
          {backingUp ? 'Backing up…' : 'Back up now'}
        </button>
      </div>

      {provider === 'local' && (
        <div style={{ padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, color: '#92400E', fontSize: 12, margin: '10px 0' }}>
          This tenant's storage is the server's own disk, not S3 — backups (including the safety copy taken before a
          restore) stay on the same machine as the database. Configure S3 under the hospital's Admin Panel → Storage
          for off-site copies.
        </div>
      )}
      {data?.storage_error && (
        <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 12, margin: '10px 0' }}>
          Could not read the backup folder: {data.storage_error}
        </div>
      )}
      {error && <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 12, margin: '10px 0' }}>{error}</div>}
      {notice && <div style={{ padding: '8px 12px', background: '#DCFCE7', border: '1px solid #BBF7D0', borderRadius: 8, color: '#166534', fontSize: 12, margin: '10px 0' }}>{notice}</div>}

      {!suspended && (
        <div style={{ fontSize: 12, color: '#6B7280', margin: '10px 0' }}>
          To restore a backup, suspend the tenant first — staff must not be writing data while it is being replaced.
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
              <th style={{ padding: '6px 8px' }}>Taken</th>
              <th style={{ padding: '6px 8px' }}>Type</th>
              <th style={{ padding: '6px 8px' }}>Size</th>
              <th style={{ padding: '6px 8px' }}>By</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={5} style={{ padding: 14, textAlign: 'center', color: '#9CA3AF' }}>Loading…</td></tr>}
            {data && data.backups.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 14, textAlign: 'center', color: '#9CA3AF' }}>No backups in storage yet. Use “Back up now”.</td></tr>
            )}
            {data?.backups.map(b => {
              const k = KIND_LABEL[b.kind];
              return (
                <tr key={b.key} style={{ borderBottom: '1px solid #F3F4F6' }} title={b.key}>
                  <td style={{ padding: '6px 8px' }}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ background: k.bg, color: k.color, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{k.label}</span>
                  </td>
                  <td style={{ padding: '6px 8px' }}>{formatBytes(b.bytes)}</td>
                  <td style={{ padding: '6px 8px', color: '#6B7280' }}>{b.created_by || (b.kind === 'scheduled' ? 'Scheduled job' : '—')}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={btn} onClick={() => download(b)} disabled={downloading === b.key}>
                      {downloading === b.key ? '…' : 'Download'}
                    </button>{' '}
                    <button
                      style={{ ...btn, color: suspended ? '#991B1B' : '#9CA3AF', borderColor: suspended ? '#FECACA' : '#E5E7EB', cursor: suspended ? 'pointer' : 'not-allowed' }}
                      disabled={!suspended}
                      title={suspended ? 'Restore this backup' : 'Suspend the tenant first'}
                      onClick={() => setRestoreTarget(b)}
                    >
                      Restore
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!!data?.restores.length && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Restore history</div>
          {data.restores.map(r => (
            <div key={r.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, color: r.status === 'succeeded' ? '#166534' : r.status === 'failed' ? '#991B1B' : '#92400E' }}>
                {r.status === 'succeeded' ? '✓ Restored' : r.status === 'failed' ? '✕ Failed' : '… Running'}
              </span>{' '}
              {new Date(r.started_at).toLocaleString()} by {r.operator_name || 'operator'} from <code>{r.backup_key}</code>
              {r.safety_backup_key && <> · previous data kept at <code>{r.safety_backup_key}</code></>}
              {r.error && <div style={{ color: '#991B1B', marginTop: 2 }}>{r.error}</div>}
            </div>
          ))}
        </div>
      )}

      {restoreTarget && (
        <RestoreBackupDialog
          open
          onOpenChange={(o) => { if (!o) { setRestoreTarget(null); refresh(); } }}
          tenantId={tenantId}
          slug={slug}
          backup={restoreTarget}
          storageLabel={provider ? (PROVIDER_LABEL[provider] ?? provider) : 'object storage'}
          onRestored={onChanged}
        />
      )}
    </div>
  );
}
